import styled from "@emotion/styled"
import { debounce, IconButton, Input, Modal } from "@mui/material"
import { useMutation } from "@tanstack/react-query"
import { useLocale, useTranslations } from "next-intl"
import Image from "next/image"
import { usePathname } from "next/navigation"
import { useEffect, useMemo, useRef, useState } from "react"
import { Culture, type Result } from "utils"
import { sharedColors } from "../../theme/colors"
import Results from "./Results"

const StyledContainer = styled.div`
  position: relative;
`

const StyledLayout = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
  justify-content: center;
  position: absolute;
  top: 106px;
  left: 50%;
  transform: translateX(-50%);
`

const StyledModalBox = styled.div<{ hasBottomRadius: boolean }>`
  border-radius: ${(props) =>
    props.hasBottomRadius ? "12px 12px 0px 0px" : "12px"};
  max-width: 912px;
  width: 100%;
  display: flex;
  height: 56px;
  padding: 12px;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  background-color: white;
  box-shadow: ${(props) =>
    props.hasBottomRadius
      ? ` inset 0px 1px 0px 0px ${sharedColors.borderGray}`
      : "none"};
`

const StyledIconButton = styled(IconButton)`
  display: flex;
  width: 56px;
  height: 56px;
  padding: 0px 22.4px;
  justify-content: center;
  align-items: center;
  gap: 8.4px;
  border-radius: 12px;
  background-color: white;
  box-shadow: 0px 5.6px 16.8px 0px rgba(0, 0, 0, 0.16);
  :hover {
    border: 2px solid ${sharedColors.brandDark};
    box-shadow:
      /* inset 0px 0px 0px 2px ${sharedColors.brandDark}, */ 0px 4px 12px 0px
      rgba(0, 0, 0, 0.16);
    background-color: white;
  }
`

const StyledSubmitButton = styled(IconButton)`
  display: flex;
  width: 40px;
  height: 40px;
  padding: 0;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 12px;
  background-color: ${sharedColors.brandDark};
  :hover {
    box-shadow:
      0px 4px 12px 0px rgba(0, 0, 0, 0.16),
      inset 0px 0px 0px 2px ${sharedColors.brandDark};
    background-color: ${sharedColors.brandDark};
  }
  :disabled {
    background-color: #f5f6f8;
    path {
      stroke: #051711;
    }
  }
`

const ContentLayout = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 912px;
`

interface SearchModalProps {
  open: boolean
  handleClose: () => void
  culture: Culture
  searchInputRef: React.RefObject<HTMLInputElement>
}

export const SearchModal = ({
  open,
  handleClose,
  culture,
  searchInputRef,
}: SearchModalProps) => {
  const [text, setText] = useState<string>("")
  const [showResults, setShowResults] = useState<boolean>(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const t = useTranslations("Homepage")

  const closeModal = () => {
    handleClose()
    console.log("close")
    setText("")
    setShowResults(false)
  }

  const resetSearch = () => {
    setShowResults(false)
    setText("")
    inputRef.current?.focus()
  }
  const clear = () => {
    setText("")
  }
  const locale = useLocale()

  const pathname = usePathname()

  const { data, mutate, reset } = useMutation({
    mutationFn: async (text: string) => {
      const res = await fetch(`/api/search?term=${text}&locale=${locale}`)
      const data: Result = await res.json()
      return data
    },
    onMutate: () => {
      setShowResults(false)
    },
    onSettled: () => {
      setShowResults(true)
    },
  })

  useEffect(() => {
    if (!open) {
      setText("")
      setShowResults(false)
      reset()
    }
  }, [open, reset])

  const array = data || []

  const debouncedMutate = useMemo(
    () =>
      debounce((searchText: string) => {
        if (searchText) {
          mutate(searchText)
        }
      }, 1000),
    [mutate],
  )

  const handleSearch = () => {
    debouncedMutate(text)
  }

  useEffect(() => {
    if (open) {
      debouncedMutate(text)
    }
    return () => {
      debouncedMutate.clear()
    }
  }, [text, open, debouncedMutate])

  return (
    <Modal open={open} onClose={closeModal}>
      <StyledContainer>
        <StyledLayout>
          <ContentLayout>
            <StyledModalBox hasBottomRadius={!!data}>
              <Input
                onChange={(e) => setText(e.target.value)}
                value={text}
                inputRef={searchInputRef}
                sx={{
                  "&.MuiInput-root:before": {
                    borderBottom: "none",
                  },
                  "&.MuiInput-root:after": {
                    borderBottom: "none",
                  },
                  "&.MuiInput-root:hover:not(.Mui-disabled):before": {
                    borderBottom: "none",
                  },
                  fontSize: "16px",
                  lineHeight: "150%",
                  color: "#051711",
                }}
                inputProps={{ "aria-label": "הקלד מילות חיפוש" }}
                fullWidth
                placeholder={t("searchEgged")}
                startAdornment={
                  text ? (
                    <IconButton onClick={clear}>
                      <Image
                        src="/icons/close.svg"
                        alt="close"
                        width={20}
                        height={20}
                      />
                    </IconButton>
                  ) : null
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSearch()
                  }
                }}
              />
            </StyledModalBox>
            {showResults ? (
              <Results
                handleClose={resetSearch}
                array={array}
                term={text}
                locale={locale}
              />
            ) : null}
          </ContentLayout>
          <StyledIconButton aria-label="לחצן סגירה" onClick={closeModal}>
            <Image
              src="/icons/closeSearch.svg"
              alt="close"
              width={20}
              height={20}
            />
          </StyledIconButton>
        </StyledLayout>
      </StyledContainer>
    </Modal>
  )
}
